// src/components/layout/GlassBox.tsx
import { Box, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";

interface GlassBoxProps extends React.ComponentProps<typeof Box> {
  /**
   * Visual variant:
   * - "filled" (default): Light glass container (matches ActionPanel / 3rd column style).
   *   Use for top-level sections (Patient Profile, Molecular Profile, Cancer types).
   * - "outlined": Solid royalBlue header with white text.
   *   Use for subsections (Diagnosis, Events).
   */
  variant?: "filled" | "outlined";
  /**
   * Whether the section this GlassBox controls is currently expanded.
   * Expanded state shifts border color for visual feedback.
   */
  expanded?: boolean;
}

/**
 * GlassBox
 * - Collapsible section header
 * - "filled" = light glass container (like 3rd column / ActionPanel wrapper)
 * - "outlined" = solid royalBlue (like MUI contained primary button)
 * - Expanded state: border accent
 * - No scale on hover (avoids container overflow)
 */
const GlassBox = ({
  children,
  sx,
  variant = "filled",
  expanded = false,
  ...props
}: GlassBoxProps) => {
  const theme = useTheme();

  // ─── Filled variant (light glass — matches 3rd column / ActionPanel container) ───
  // Previous deep-blue style (keep commented for potential rollback):
  const filledStyles = {
    background: `linear-gradient(180deg, ${alpha(
      theme.palette.primary.dark,
      0.95
    )} 0%, ${alpha(theme.palette.primary.dark, 0.85)} 100%)`,
    border: `1px solid ${
      expanded
        ? alpha(theme.palette.common.white, 0.35)
        : alpha(theme.palette.common.white, 0.2)
    }`,
    color: theme.palette.common.white,
    "&:hover": {
      background: `linear-gradient(180deg, ${
        theme.palette.primary.dark
      } 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
      borderColor: alpha(theme.palette.common.white, 0.4),
    },
    "&:active": { background: theme.palette.primary.dark },
  };
  // const filledStyles = {
  //   backgroundColor: alpha(
  //     theme.palette.background.paper,
  //     theme.custom?.containerOpacity?.light || 0.6
  //   ),
  //   backdropFilter: "blur(8px)",
  //   border: `1px solid ${
  //     expanded ? alpha(theme.palette.primary.main, 0.35) : "none"
  //   }`,
  //   color: theme.custom?.contentText?.primary || theme.palette.text.primary,
  //   "&:hover": {
  //     backgroundColor: alpha(
  //       theme.palette.background.paper,
  //       (theme.custom?.containerOpacity?.light || 0.6) + 0.1
  //     ),
  //     borderColor: alpha(theme.palette.primary.main, 0.4),
  //   },
  //   "&:active": {
  //     backgroundColor: alpha(
  //       theme.palette.background.paper,
  //       theme.custom?.containerOpacity?.medium || 0.75
  //     ),
  //   },
  // };

  // ─── Outlined variant (solid royalBlue — same as previous filled style) ───
  // Previous royalBlue-tinted transparent style (keep commented for potential rollback):
  // const outlinedStyles = {
  //   background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${alpha(theme.palette.primary.main, 0.07)} 100%)`,
  //   backdropFilter: "blur(8px)",
  //   border: `1px solid ${expanded ? alpha(theme.palette.primary.main, 0.4) : alpha(theme.palette.primary.main, 0.25)}`,
  //   color: theme.custom?.contentText?.primary || theme.palette.text.primary,
  //   "&:hover": {
  //     borderColor: alpha(theme.palette.primary.main, 0.5),
  //     background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.13)} 0%, ${alpha(theme.palette.primary.main, 0.10)} 100%)`,
  //   },
  //   "&:active": { borderColor: alpha(theme.palette.primary.main, 0.6) },
  // };

  // Outlined variant — mirrors NeumorphicCard "dark" variant styling
  const isDark = theme.palette.mode === "dark";
  const outlinedBaseOpacity = isDark ? 0.1 : 0.06;
  const outlinedStyles = {
    background: `linear-gradient(180deg, ${alpha(
      theme.palette.primary.dark,
      outlinedBaseOpacity
    )} 0%, ${alpha(theme.palette.primary.dark, outlinedBaseOpacity)} 100%)`,
    backdropFilter: "blur(8px)",
    border: `1px solid ${
      expanded
        ? isDark
          ? alpha(theme.palette.secondary.main, 0.75)
          : alpha(theme.palette.primary.dark, 0.45)
        : isDark
          ? alpha(theme.palette.secondary.main, 0.55)
          : alpha(theme.palette.primary.dark, 0.35)
    }`,
    // boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    color: theme.custom?.contentText?.primary || theme.palette.text.primary,
    "&:hover": {
      borderColor: isDark
        ? alpha(theme.palette.secondary.main, 0.75)
        : alpha(theme.palette.primary.dark, 0.45),
    },
    "&:active": {
      boxShadow: `inset 0 1px 3px ${alpha(theme.palette.primary.dark, 0.1)}`,
    },
  };

  // const outlinedStyles = {
  //   background: `linear-gradient(180deg, ${alpha(
  //     theme.palette.primary.dark,
  //     0.95
  //   )} 0%, ${alpha(theme.palette.primary.dark, 0.85)} 100%)`,
  //   border: `1px solid ${
  //     expanded
  //       ? alpha(theme.palette.common.white, 0.35)
  //       : alpha(theme.palette.common.white, 0.2)
  //   }`,
  //   color: theme.palette.common.white,
  //   "&:hover": {
  //     background: `linear-gradient(180deg, ${
  //       theme.palette.primary.dark
  //     } 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
  //     borderColor: alpha(theme.palette.common.white, 0.4),
  //   },
  //   "&:active": {
  //     background: theme.palette.primary.dark,
  //   },
  // };

  const variantStyles = variant === "filled" ? filledStyles : outlinedStyles;

  return (
    <Box
      role="button"
      tabIndex={0}
      sx={{
        px: 3,
        py: 0.5,

        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",

        // Shape
        borderRadius: theme.shape.borderRadius / 6,

        // Smooth transitions (no transform/scale)
        transition:
          "border-color 0.15s ease, background 0.2s ease, color 0.2s ease",

        // Apply variant styles
        ...variantStyles,

        // Focus outline
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.6)}`,
        },

        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export default GlassBox;
